/**
 * Tokyo Reads — back gesture for overlays (theme-zijsessie 4, 2026-09-25; follow-up of kaart #62).
 *
 * Same pattern as tr-drawer-history.js (cart drawer, kaart #62), which stays untouched:
 *   open   -> push our own history entry (same URL, state {trOverlay: <name>})
 *   back   -> the theme's OWN close (identical animation to tapping X)
 *   X / outside tap / Esc -> consume our entry with history.back() (swallowed)
 * Overlays handled here:
 *   'menu'     mobile menu: drawer-element[data-drawer-section] / [data-drawer-header] (DrawerElement.close())
 *   'lightbox' product image lightbox: PhotoSwipe v4 .pswp.pswp-zoom-gallery (its own close button)
 * The series-page filter sheet does the same inside tr-si-script (own component) and only uses the
 * measurement hook below. The cart is tr-drawer-history.js: that script is loaded earlier and swallows
 * its own pops (stopImmediatePropagation, capture), so its pops never reach this listener.
 *
 * Menu links: tapping a link while our menu entry is current first consumes the entry, then navigates.
 * Otherwise the stale entry costs a second back-press on the next page (menus are left by navigating).
 *
 * Measurement: 'tr:overlay:close' {overlay, method: 'back' | 'x'} -> Shopify custom event
 * 'overlay_close' for Jan's custom pixel (runbook par 10/12), same route as cart_drawer_close.
 *
 * Touch screens only (Jan, 2026-09-19). Loaded from snippets/head.liquid right AFTER
 * tr-drawer-history.js (defer), before the section scripts.
 */
(function () {
  'use strict';

  var isTouch = false;
  try {
    isTouch = window.matchMedia('(pointer: coarse)').matches;
  } catch (e) {
    isTouch = false;
  }
  if (!isTouch) {
    return;
  }

  var KEY = 'trOverlay';
  var MENU_SEL = 'drawer-element[data-drawer-section], drawer-element[data-drawer-header]';
  var stack = [];          // our overlays whose history entry is live, top = last
  var suppress = 0;        // pops caused by our own history.back() calls
  var pendingNav = null;   // menu link to follow once our entry is consumed
  var navTimer = null;

  function top() {
    return stack.length ? stack[stack.length - 1] : null;
  }

  function currentIs(name) {
    var s = window.history.state;
    return !!(s && s[KEY] === name);
  }

  function report(overlay, method) {
    try {
      document.dispatchEvent(new CustomEvent('tr:overlay:close', { bubbles: true, detail: { overlay: overlay, method: method } }));
    } catch (e) {
      /* old engines: no measurement, no harm */
    }
  }

  // Consume our entry without the browser restoring an old scroll position on the way back.
  function consume() {
    suppress += 1;
    try {
      window.history.scrollRestoration = 'manual';
    } catch (e) { /* ignore */ }
    window.history.back();
  }

  function opened(name) {
    if (stack.indexOf(name) !== -1) {
      return;
    }
    try {
      window.history.pushState({ trOverlay: name }, '');
      stack.push(name);
    } catch (e) {
      /* Safari rate-limits pushState: the overlay still works, back just navigates */
    }
  }

  function closedByUi(name) {
    var i = stack.indexOf(name);
    if (i === -1) {
      return; // entry already popped by a back-press (or never pushed)
    }
    stack.splice(i, 1);
    if (currentIs(name)) {
      report(name, 'x');
      consume();
    }
    // Not current (another layer's entry is on top): leave history alone; the stale entry
    // is harmless (a back-press on it finds no open overlay and passes through).
  }

  function openMenu() {
    return document.querySelector('drawer-element[data-drawer-section].is-open, drawer-element[data-drawer-header].is-open');
  }

  function openLightbox() {
    return document.querySelector('.pswp.pswp--open.pswp-zoom-gallery');
  }

  function closeOverlay(name) {
    if (name === 'menu') {
      var m = openMenu();
      if (m && typeof m.close === 'function') {
        m.close();
      }
    } else if (name === 'lightbox') {
      var lb = openLightbox();
      var btn = lb && (lb.querySelector('.pswp__custom-close') || lb.querySelector('.pswp__button--close'));
      if (btn) {
        btn.click();
      }
    }
  }

  window.addEventListener('popstate', function (e) {
    if (suppress > 0) {
      suppress -= 1;
      e.stopImmediatePropagation();
      try {
        window.history.scrollRestoration = 'auto';
      } catch (err) { /* ignore */ }
      if (pendingNav) {
        var href = pendingNav;
        pendingNav = null;
        if (navTimer) {
          clearTimeout(navTimer);
          navTimer = null;
        }
        window.location.assign(href);
      }
      return;
    }
    var t = top();
    if (!t) {
      return; // not ours: the theme (or the cart script) handles it
    }
    // A user back-press while one of our overlays is on top: close it the theme's way.
    stack.pop();
    e.stopImmediatePropagation();
    report(t, 'back');
    closeOverlay(t);
  }, true);

  // --- mobile menu: watch the drawer's is-open class ---------------------------------------
  function watchMenus() {
    var menus = document.querySelectorAll(MENU_SEL);
    for (var i = 0; i < menus.length; i += 1) {
      (function (el) {
        var wasOpen = el.classList.contains('is-open');
        new MutationObserver(function () {
          var isOpen = el.classList.contains('is-open');
          if (isOpen === wasOpen) {
            return;
          }
          wasOpen = isOpen;
          if (isOpen) {
            opened('menu');
          } else {
            closedByUi('menu');
          }
        }).observe(el, { attributes: true, attributeFilter: ['class'] });
      })(menus[i]);
    }
  }

  // Menu links: consume our entry first, then navigate (bubble phase: the theme's own handlers,
  // e.g. dropdown toggles that preventDefault, have run already).
  document.addEventListener('click', function (e) {
    if (top() !== 'menu' || !currentIs('menu')) {
      return;
    }
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
      return;
    }
    var a = e.target && e.target.closest ? e.target.closest('a[href]') : null;
    if (!a || !a.closest(MENU_SEL)) {
      return;
    }
    if (a.target && a.target !== '_self') {
      return;
    }
    if (a.hasAttribute('download')) {
      return;
    }
    var url;
    try {
      url = new URL(a.getAttribute('href'), window.location.href);
    } catch (err) {
      return;
    }
    if (url.origin !== window.location.origin || url.protocol.indexOf('http') !== 0) {
      return;
    }
    if (url.pathname === window.location.pathname && url.search === window.location.search && url.hash) {
      return; // in-page anchor: let the theme handle it
    }
    e.preventDefault();
    stack.pop();
    pendingNav = url.href;
    consume();
    // Safety net: if the pop never arrives, navigate anyway.
    navTimer = setTimeout(function () {
      if (pendingNav) {
        var href = pendingNav;
        pendingNav = null;
        window.location.assign(href);
      }
    }, 450);
  }, false);

  // --- product image lightbox: watch PhotoSwipe roots -----------------------------------------
  function watchLightboxes() {
    var roots = document.querySelectorAll('.pswp');
    for (var i = 0; i < roots.length; i += 1) {
      (function (el) {
        var wasOpen = false;
        new MutationObserver(function () {
          var isOpen = el.classList.contains('pswp--open') && el.classList.contains('pswp-zoom-gallery');
          if (isOpen === wasOpen) {
            return;
          }
          wasOpen = isOpen;
          if (isOpen) {
            opened('lightbox');
          } else {
            closedByUi('lightbox');
          }
        }).observe(el, { attributes: true, attributeFilter: ['class'] });
      })(roots[i]);
    }
  }

  // Back-forward cache: a restored page starts clean.
  window.addEventListener('pageshow', function (e) {
    if (e.persisted) {
      suppress = 0;
      pendingNav = null;
      stack = [];
      if (openMenu()) {
        // restored with the menu open: our entry below is current again only if the state says so
        if (currentIs('menu')) {
          stack.push('menu');
        }
      }
    }
  });

  // Measurement (see header): forward to the Shopify customer-events bus for Jan's custom pixel.
  document.addEventListener('tr:overlay:close', function (e) {
    var d = (e && e.detail) || {};
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish('overlay_close', { overlay: d.overlay || '', method: d.method || 'x' });
      }
    } catch (err) { /* no analytics: no measurement, no harm */ }
    try {
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'overlay_close', { overlay: d.overlay || '', method: d.method || 'x' });
      }
    } catch (err) { /* idem */ }
  });

  function init() {
    watchMenus();
    watchLightboxes();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
